/**
 * Shared User Management loader service.
 * Normalizes raw Cognito user objects into canonical User models (SimpleUser).
 * Single Source of Truth for user listing and profile resolution across all modules.
 */

import { User, UserRole } from '../types/user';
import { AdminService } from './admin';
import { CognitoAdminService } from './cognitoAdmin';
import { isDemoMode, DEMO_USERS } from './mockData';
import { canonicalizeEmail, sameIdentity, CANONICAL_EMAIL_DOMAIN } from './identity';
import { isCurrentEmployee, todayIso } from './employmentStatus';

export interface SimpleUser {
  id: string;
  employeeId: string;
  fullName: string;
  name: string;
  email: string;
  department: string;
  designation: string;
  primaryShift: string;
  role: string;
  managerEmail?: string;
  location?: string;
  enabled?: boolean;
  dateOfJoining?: string;
  /**
   * Last working day, when the employee has left. Stored in UserProfiles alongside
   * shift and dateOfJoining — never a Cognito attribute. Caps leave accrual: nobody
   * earns leave for months after they have gone.
   */
  dateOfExit?: string;
  /**
   * Full Time / Intern / Consultant. Stored in UserProfiles alongside dateOfExit —
   * never a Cognito attribute (the pool schema cannot safely gain one). Drives the
   * Intern leave schedule in leaveAccrualEngine.ts.
   */
  employmentType?: string;
  /**
   * Team / product line — one of TEAM_OPTIONS.
   *
   * Separate from `department` above, which is an existing free-text Cognito attribute
   * (Engineering, HR, General…). Reusing that field for a fixed team list would have
   * overwritten whatever every user already had in it. Stored in UserProfiles for the same
   * reason as dateOfExit and employmentType: the pool cannot safely gain a custom attribute.
   */
  team?: string;
  /**
   * The employee's code on the BIOMETRIC DEVICE, when it differs from `employeeId`.
   *
   * Team Office enrols people under its own numbering ("0020"); Employee Master uses its own
   * ("EMP0001"). Treating them as one value left every real punch matching nobody. Blank means
   * "the two are the same", which is the common case and the fallback the sync applies.
   *
   * Stored in UserProfiles, like every other field the Cognito pool cannot safely take.
   */
  deviceCode?: string;
  /**
   * Mobile number in E.164 ("+919876543210"), for the attendance shortfall SMS.
   *
   * UserProfiles-backed like deviceCode above. Blank means this employee gets the email
   * notice and no SMS, which is the state every employee is in until a number is recorded.
   */
  phone?: string;
  /**
   * Aadhaar number, 12 digits, and PAN in ABCDE1234F form.
   *
   * UserProfiles-backed like phone and deviceCode above — the Cognito pool cannot safely gain a
   * custom attribute, and these two least of all. Both optional: most rows predate the fields,
   * and undefined here means "not collected", never "cleared".
   */
  aadhaarNumber?: string;
  panNumber?: string;
  /**
   * Date of birth (ISO). Stored in UserProfiles for the same reason as dateOfExit and team —
   * the Cognito pool cannot safely gain a custom attribute.
   */
  dateOfBirth?: string;
  /**
   * Every Cognito account id this person signs in with, most authoritative first.
   *
   * One human has one account per email domain, each with its own Cognito username.
   * Anything keyed by username rather than email — shift assignments, notably — has to
   * look under all of them. Absent on records built outside loadAllUsers; read it via
   * accountIdsFor() rather than directly.
   */
  linkedIds?: string[];
  /**
   * Every Cognito `sub` this person signs in with.
   *
   * Distinct from `linkedIds`, which holds Cognito *usernames* — and the distinction is the whole
   * point. The signed-in user's id is `getCurrentUser().userId`, which is the sub, while `id` above
   * is the username; for a federated account those are never the same string (`azuread_…` vs a
   * UUID). So anything that has to match an actor recorded from the session against this directory
   * — the leave audit trail's Changed By column, notably — cannot do it on `id` alone.
   *
   * Both accounts of a merged native/federated pair contribute, since either could have been the
   * one that acted. Absent on records built outside loadAllUsers.
   */
  subs?: string[];
}

/**
 * The teams an employee can belong to.
 *
 * @deprecated Read `OrgConfigService.getTeams()` instead. HR maintains the list in
 * Settings → Organisation Setup, so a hardcoded array here can only ever be a stale copy.
 * Retained as a re-export of the defaults so any straggling import keeps compiling and keeps
 * returning the shipped values; the single source of truth is DEFAULT_TEAMS in
 * orgConfigService, which that service falls back to.
 */
export { DEFAULT_TEAMS as TEAM_OPTIONS } from './orgConfigService';

/** Every Cognito account id for this person, falling back to the single id. */
/**
 * Order two people by their HR employee number.
 *
 * One definition, used by both User Management and the Shift Details grid. Cognito's `listUsers`
 * has no defined order and returns a different one between loads, so without this a name sat in a
 * different place every time a screen was opened and the only way to find someone was to search.
 *
 * `localeCompare` with `numeric`, not a plain string compare: EMP0002 must precede EMP0010, and
 * CRT0044 must precede CRT0100 — a lexicographic sort gets both wrong wherever the number is not
 * zero-padded to a fixed width. Case-insensitive for the same reason the codes are free text.
 *
 * A missing number sorts *last*, then by name. It is a data gap rather than a low number, and an
 * empty string would otherwise collate to the very top, where the smallest IDs belong.
 */
export const compareByEmployeeId = (
  a: { employeeId?: string; name?: string },
  b: { employeeId?: string; name?: string }
): number => {
  const ca = (a.employeeId || '').trim();
  const cb = (b.employeeId || '').trim();
  const byName = (a.name || '').localeCompare(b.name || '');
  if (!ca && !cb) return byName;
  if (!ca) return 1;
  if (!cb) return -1;
  return ca.localeCompare(cb, undefined, { numeric: true, sensitivity: 'base' }) || byName;
};

export const accountIdsFor = (user: SimpleUser): string[] =>
  user.linkedIds && user.linkedIds.length > 0 ? user.linkedIds : [user.id];

/**
 * The department values a filter dropdown should offer, derived from a directory.
 *
 * Every screen that filters by department was deriving this itself, and they disagreed. The
 * visible symptom was on Leave Policies → Leave Assignment, whose "All Departments" list offered
 * values User Management had never heard of — most obviously **General**, which is not a
 * department anybody is in but the placeholder this loader used to write onto every record with
 * no `custom:department` set. Selecting it filtered the sheet down to "everyone HR has not
 * finished setting up", presented as if it were a real team.
 *
 * The lists also differed in ways that are quieter but just as confusing: User Management sorted
 * its options and Leave Assignment offered them in whatever order Cognito returned users, and
 * neither collapsed case, so "Engineering" and "engineering" appeared as two departments that
 * each matched a different half of the same group.
 *
 * One function, used by both, so the two cannot drift again:
 *  - blanks dropped — an unset department is not an option, it is the absence of one;
 *  - deduplicated case-insensitively, keeping the first spelling seen;
 *  - sorted, so a reader can find a department by name rather than by scanning.
 *
 * Note this offers no "unassigned" entry. Neither screen had one before, and adding it here
 * would change what the two lists mean rather than making them agree.
 */
export const departmentOptions = (
  people: Array<{ department?: string | null }>
): string[] => {
  const bySpelling = new Map<string, string>();
  for (const person of people) {
    const value = (person?.department || '').trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (!bySpelling.has(key)) bySpelling.set(key, value);
  }
  return Array.from(bySpelling.values()).sort((a, b) => a.localeCompare(b));
};

/**
 * Extract attribute value from a Cognito user object.
 * Handles both direct properties and Attributes array format.
 */
/**
 * Whether a recorded actor id refers to this person.
 *
 * An audit row stores whatever `resolveActorId` had at the time — an email when the account has
 * one, otherwise the session's Cognito sub. Placing that string back on a person means trying
 * every identifier they own, because which one was stored depends on how they signed in:
 *
 *   email       — a native account with the attribute set
 *   subs        — the session id (`getCurrentUser().userId`) for an account with no email
 *   id          — the Cognito username, for rows written from a directory-side flow
 *   linkedIds   — the other domain account's username, for a merged pair
 *   employeeId  — the HR number, for rows written from an HR screen
 *
 * A rule rather than five comparisons inlined at the call site: it was written once against `id`
 * alone, which can never match a federated sub, and the Changed By column showed a raw UUID for
 * months as a result. Comparison is case-insensitive and trimmed; a blank actor matches nobody.
 */
export function matchesActorId(user: SimpleUser, actorId?: string): boolean {
  const needle = (actorId || '').trim().toLowerCase();
  if (!needle) return false;
  const same = (value?: string): boolean => (value || '').trim().toLowerCase() === needle;
  return (
    same(user.email) ||
    same(user.id) ||
    same(user.employeeId) ||
    (user.subs || []).some(same) ||
    (user.linkedIds || []).some(same)
  );
}

function getAttributeValue(cognitoUser: any, attrName: string): string | undefined {
  const directAttrName = attrName.replace('custom:', '');
  if (cognitoUser[directAttrName] !== undefined && cognitoUser[directAttrName] !== '') return cognitoUser[directAttrName];
  if (cognitoUser[attrName] !== undefined && cognitoUser[attrName] !== '') return cognitoUser[attrName];
  if (cognitoUser.Attributes && Array.isArray(cognitoUser.Attributes)) {
    const attr = cognitoUser.Attributes.find((a: any) => a.Name === attrName);
    if (attr && attr.Value !== undefined && attr.Value !== '') return attr.Value;
  }
  return undefined;
}

/**
 * Read a value the Lambda merged in from the UserProfiles DynamoDB table.
 *
 * These arrive at the top level of a listUsers record and never inside `Attributes`,
 * which is the only thing distinguishing them from a Cognito attribute of the same name.
 */
function getProfileValue(cognitoUser: any, key: string): string | undefined {
  const value = cognitoUser?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

/** Read a genuine Cognito attribute — top-level custom key or `Attributes` entry. */
function getCognitoAttribute(cognitoUser: any, attrName: string): string | undefined {
  const direct = cognitoUser?.[attrName];
  if (typeof direct === 'string' && direct.trim() !== '') return direct.trim();
  if (Array.isArray(cognitoUser?.Attributes)) {
    const attr = cognitoUser.Attributes.find((a: any) => a?.Name === attrName);
    if (attr && typeof attr.Value === 'string' && attr.Value.trim() !== '') return attr.Value.trim();
  }
  return undefined;
}

/**
 * Cognito attributes that have ever carried a shift code, most recently used first.
 *
 * All of them are legacy: shift lives in the UserProfiles table. The dev pool still
 * carries `custom:shift` — added out-of-band, and Cognito has no API to remove a schema
 * item, so it is permanent (see terraform/modules/cognito/main.tf). It therefore holds a
 * stale value for anyone whose shift was changed after the move to UserProfiles, which is
 * why these are consulted last and only when the profile store has nothing.
 */
const LEGACY_SHIFT_ATTRIBUTES = [
  'custom:shift',
  'custom:primaryShift',
  'custom:default_shift',
  'custom:defaultShift',
];

/**
 * The single place an employee's assigned shift is resolved from a raw listUsers record.
 *
 * There used to be two: this loader tried `custom:primaryShift` first and had no profile
 * store fallback at all, while User Management started at `custom:shift` and did. For any
 * employee carrying more than one of these values the two screens picked different ones,
 * so the shift roster and User Management showed the same person on different shifts.
 *
 * @param profileShift UserProfiles value fetched separately, for the case where the Lambda
 *                     did not merge it into the record.
 */
export function resolveShiftCode(cognitoUser: any, profileShift?: string): string {
  const merged = getProfileValue(cognitoUser, 'shift');
  if (merged) return merged;

  const fromProfileTable = typeof profileShift === 'string' ? profileShift.trim() : '';
  if (fromProfileTable) return fromProfileTable;

  for (const attrName of LEGACY_SHIFT_ATTRIBUTES) {
    const legacy = getCognitoAttribute(cognitoUser, attrName);
    if (legacy) return legacy;
  }

  // No localStorage fallback. A per-browser cache here would let two admins on two
  // machines see different shift values for the same employee.
  return '';
}

/**
 * Pick one shift when a person's two Cognito accounts each carry one.
 *
 * The native account wins: it is the row HR edits in User Management, so it holds the
 * value an admin last chose, while the federated `azuread_*` twin only ever carries what
 * the post-auth sync last copied across. Both screens have to apply the same rule — they
 * previously did not (highest role wins here, last-seen wins there), which made the two
 * disagree for exactly the people who have both account types.
 */
export function mergeShiftCode(
  a: { id?: string; shift?: string },
  b: { id?: string; shift?: string }
): string {
  const isFederated = (id?: string) => String(id || '').startsWith('azuread_');
  // Stable sort, so two accounts of the same kind keep their original precedence.
  const nativeFirst = [a, b].sort((x, y) => Number(isFederated(x.id)) - Number(isFederated(y.id)));
  return nativeFirst.map(u => (u.shift || '').trim()).find(Boolean) || '';
}

/** Seniority, for deciding which of a person's accounts speaks for them. */
const roleRank: Record<string, number> = {
  [UserRole.SUPER_ADMIN]: 4,
  [UserRole.FINANCE_ADMIN]: 3,
  [UserRole.MANAGER]: 2,
  [UserRole.EMPLOYEE]: 1,
};

/** What the twin rule needs. Deliberately narrow, so it is testable without a whole SimpleUser. */
export interface TwinCandidate {
  id?: string;
  role?: string;
  email?: string;
  name?: string;
  fullName?: string;
}

/**
 * Which of a person's two accounts speaks for them — name, email, manager and the rest.
 *
 * ── Why this is shared, and why it is TOTAL ───────────────────────────────────────────────
 *
 * Every employee has one account per email domain, and both screens collapse them onto one
 * canonical key. Neither screen merges `email` or `name`: each spreads the winner, so whichever
 * twin wins IS the person as far as the page is concerned.
 *
 * The two screens used to decide that differently — User Management by "native, then any role
 * above Employee", the roster by "highest role" — and both fell through to *whichever Cognito
 * listed first* when those tied. That is not a rule, it is an accident of list order, and it is
 * why the same person could appear as `Usha Naga Sri Bandaru <…@creyentetech.com>` on one page
 * and `usha.bandaru <…@creyenteinfotech.com>` on the other. Someone who had just created the
 * second account read that as "my new user did not save".
 *
 * So the ordering below is total: every step has a tiebreak under it, ending at the Cognito
 * username, which is unique. Two screens applying it to the same pair cannot disagree, and the
 * answer does not move between page loads.
 *
 * The first two steps are unchanged in meaning, because other things depend on them: the native
 * account is the row HR edits and the direction the post-auth sync copies in, which is what
 * makes it the right source for `managerEmail` — see the note at its merge site.
 */
export function primaryTwin<T extends TwinCandidate>(a: T, b: T): T {
  const federated = (u: T) => Number(String(u.id || '').startsWith('azuread_'));
  // 1. The native account — where admin edits land.
  if (federated(a) !== federated(b)) return federated(a) < federated(b) ? a : b;

  // 2. The account carrying the real role. A federated twin often reports plain Employee.
  const rank = (u: T) => roleRank[u.role || ''] || 0;
  if (rank(a) !== rank(b)) return rank(a) > rank(b) ? a : b;

  /*
   * 3. The account that knows the person's NAME.
   *
   * Cognito's listUsers falls back to `name: attrs.name || attrs.email`, so an account created
   * without a name attribute reports its own address as its name. Letting that win renders the
   * directory row as an email where a person's name belongs — the visible half of the bug this
   * rule exists to fix.
   */
  const named = (u: T) => Number(Boolean(realName(u)));
  if (named(a) !== named(b)) return named(a) > named(b) ? a : b;

  // 4. The canonical domain — the address the rest of the system writes under.
  const canonical = (u: T) =>
    Number(String(u.email || '').toLowerCase().endsWith(`@${CANONICAL_EMAIL_DOMAIN}`));
  if (canonical(a) !== canonical(b)) return canonical(a) > canonical(b) ? a : b;

  // 5. Unique, and therefore always decisive. Never Cognito's list order.
  return String(a.id || '') <= String(b.id || '') ? a : b;
}

/**
 * A name that is a name, not the address standing in for one.
 *
 * Three spellings of the same non-answer, all of which have been seen on real accounts:
 * the full address (Cognito's own `name: attrs.name || attrs.email` fallback), the address on
 * the person's OTHER domain, and the bare local part — `usha.bandaru` for
 * usha.bandaru@…, which is what an account created without a display name ends up carrying.
 */
function realName(u: TwinCandidate): string {
  const name = (u.fullName || u.name || '').trim();
  if (!name) return '';

  const email = String(u.email || '').trim().toLowerCase();
  const localPart = email.slice(0, email.lastIndexOf('@'));
  if (localPart && name.toLowerCase() === localPart) return '';

  return sameIdentity(name, u.email) || name.toLowerCase() === email ? '' : name;
}

/**
 * UserProfiles shift map, fetched at most once per session — success or failure.
 *
 * This is a *backstop*, not the main path: the user-management Lambda already merges the
 * profile store's `shift` into every listUsers record (alongside dateOfJoining, dateOfExit,
 * employmentType, team, dateOfBirth), and resolveShiftCode reads that first. This only
 * covers a deployment whose Lambda does not.
 *
 * Memoized because it is a backstop on a hot path. loadAllUsers runs on most screens in the
 * app, and Query.getAllUsers has no resolver attached in some environments — so an
 * un-memoized version fired a doomed request, and logged an error, on every single screen
 * load. One attempt per session, then the resolved (possibly empty) map is reused.
 */
let profileShiftMapPromise: Promise<Map<string, string>> | null = null;

const loadProfileShiftMap = (): Promise<Map<string, string>> => {
  if (!profileShiftMapPromise) {
    profileShiftMapPromise = AdminService.getAllUsers()
      .then((profiles: User[]) => {
        const map = new Map<string, string>();
        profiles.forEach(p => {
          if (p?.email && p?.shift) map.set(canonicalizeEmail(p.email), p.shift);
        });
        return map;
      })
      .catch(err => {
        console.warn(
          '[userLoader] UserProfiles shift backstop unavailable — using the shift merged ' +
          'into listUsers instead. Not retried this session.',
          err
        );
        return new Map<string, string>();
      });
  }
  return profileShiftMapPromise;
};

/**
 * Load all users from the system into canonical normalized User models.
 * Handles all Cognito attribute mapping, DynamoDB lookups, and defaults internally.
 */
async function fetchAllUsers(): Promise<SimpleUser[]> {
  if (isDemoMode()) {
    return DEMO_USERS.map(u => ({
      id: u.id,
      employeeId: u.id,
      fullName: u.name,
      name: u.name,
      email: u.email,
      department: u.department || '',
      designation: u.role || 'Employee',
      primaryShift: u.shift || 'GS',
      role: u.role,
      managerEmail: u.managerEmail,
      enabled: true,
      dateOfJoining: (u as any).dateOfJoining || undefined,
    }));
  }

  try {
    // Issued alongside listUsers, never after it — loadAllUsers is on the critical path of
    // most screens and must not cost a second round trip. See loadProfileShiftMap for why
    // this is a memoized backstop rather than a real source.
    const [response, profileShiftByEmail] = await Promise.all([
      CognitoAdminService.listUsers(),
      loadProfileShiftMap(),
    ]);

    const cognitoUsers = response.users || [];
    console.log('[userLoader] total Cognito users:', response.total ?? cognitoUsers.length);
    console.log('[userLoader] total fetched users:', cognitoUsers.length);
    if (cognitoUsers.length === 0) return [];

    const mapped: SimpleUser[] = cognitoUsers.map((cognitoUser: any) => {
      const email = getAttributeValue(cognitoUser, 'email') || cognitoUser.Username || '';
      const name = getAttributeValue(cognitoUser, 'name') || getAttributeValue(cognitoUser, 'given_name') || 'Unknown';
      /*
       * Empty when unset, not 'General'.
       *
       * The placeholder was invented here and nowhere else — User Management builds the same
       * rows from the same Cognito response and leaves the field '' — so every screen fed by
       * this loader believed in a department User Management could not see. Leave Assignment
       * offered it in its filter; the reports counted it as a real group.
       *
       * Nothing downstream is left without a label: each caller already supplies its own
       * fallback for display ('—' in the leave tables and drawers, 'Unassigned' in the reports,
       * 'General' in the shift roster), which is the right place for that decision because it
       * is a rendering question, not a data one.
       */
      const department = getAttributeValue(cognitoUser, 'custom:department') || getAttributeValue(cognitoUser, 'department') || '';
      const rawRole = getAttributeValue(cognitoUser, 'custom:role') || getAttributeValue(cognitoUser, 'role') || '';
      const role = CognitoAdminService.mapCognitoRole(rawRole);
      const managerEmail = getAttributeValue(cognitoUser, 'custom:managerEmail') || getAttributeValue(cognitoUser, 'managerEmail') || cognitoUser.managerEmail || undefined;
      /*
       * No Cognito Username fallback.
       *
       * It used to end `|| cognitoUser.Username`, and for a federated Azure AD account that
       * username is an opaque `azuread_<guid>` — so the Leave Assignment sheet showed a GUID
       * under "Emp No." for exactly the people whose employee number was missing. A username
       * is not an employee number for native accounts either (it is a UUID or an email), so
       * the fallback could only ever produce a wrong answer that looked like a right one.
       * Callers already render '—' for an empty value, which is the honest reading: nobody
       * reconciles payroll against a GUID.
       */
      const employeeId = getAttributeValue(cognitoUser, 'custom:employeeId') || getAttributeValue(cognitoUser, 'employeeId') || cognitoUser.employeeId || '';
      const designation = getAttributeValue(cognitoUser, 'custom:designation') || getAttributeValue(cognitoUser, 'designation') || cognitoUser.designation || rawRole || 'Employee';
      const location = getAttributeValue(cognitoUser, 'custom:location') || getAttributeValue(cognitoUser, 'location') || cognitoUser.location || '';
      const enabled = cognitoUser.Enabled !== false && cognitoUser.enabled !== false;
      // Resolved by the shared resolver, which User Management calls too — see
      // resolveShiftCode for why there must be exactly one of these.
      const primaryShift = resolveShiftCode(
        cognitoUser,
        email ? profileShiftByEmail.get(canonicalizeEmail(email)) : undefined
      );

      const rawDateOfJoining = getAttributeValue(cognitoUser, 'custom:dateOfJoining') ||
                               getAttributeValue(cognitoUser, 'dateOfJoining') ||
                               '';
      const dateOfJoining = (typeof rawDateOfJoining === 'string' && rawDateOfJoining.trim() !== '') ? rawDateOfJoining.trim() : undefined;

      // Merged into the listUsers response by the Lambda from UserProfiles, so
      // getAttributeValue picks it up from the top level before ever looking at
      // Cognito attributes — where it deliberately does not exist.
      const rawDateOfExit = getAttributeValue(cognitoUser, 'dateOfExit') || '';
      const dateOfExit = rawDateOfExit.trim() !== '' ? rawDateOfExit.trim() : undefined;

      // Same story as dateOfExit: merged in by the Lambda from UserProfiles, no
      // Cognito attribute exists or ever will.
      const rawEmploymentType = getAttributeValue(cognitoUser, 'employmentType') || '';
      const employmentType = rawEmploymentType.trim() !== '' ? rawEmploymentType.trim() : undefined;

      // Team (product line). Also UserProfiles-backed — see SimpleUser.team for why this
      // is not folded into `department` above.
      const rawTeam = getAttributeValue(cognitoUser, 'team') || '';
      const team = rawTeam.trim() !== '' ? rawTeam.trim() : undefined;

      // Also UserProfiles-backed, merged in by the Lambda.
      const rawDob = getAttributeValue(cognitoUser, 'dateOfBirth') || '';
      const dateOfBirth = rawDob.trim() !== '' ? rawDob.trim() : undefined;

      // The biometric device's own code for this person — see SimpleUser.deviceCode. Blank
      // means it matches the HR code, which is what the attendance sync falls back to.
      const rawDeviceCode = getAttributeValue(cognitoUser, 'deviceCode') || '';
      const deviceCode = rawDeviceCode.trim() !== '' ? rawDeviceCode.trim() : undefined;

      // Merged in from UserProfiles by the Lambda, same as deviceCode. Undefined rather than
      // '' so an absent number is distinguishable from one that was deliberately cleared.
      const rawPhone = getAttributeValue(cognitoUser, 'phone') || '';
      const phone = rawPhone.trim() !== '' ? rawPhone.trim() : undefined;

      // Payroll identifiers, merged in from UserProfiles by the Lambda exactly like phone.
      // Undefined rather than '' so "never collected" stays distinguishable from "cleared".
      const rawAadhaar = getAttributeValue(cognitoUser, 'aadhaarNumber') || '';
      const aadhaarNumber = rawAadhaar.trim() !== '' ? rawAadhaar.trim() : undefined;
      const rawPan = getAttributeValue(cognitoUser, 'panNumber') || '';
      const panNumber = rawPan.trim() !== '' ? rawPan.trim().toUpperCase() : undefined;

      // The Cognito sub. Present in the ListUsers `Attributes` array; kept alongside the username
      // rather than replacing it, because assignments and profiles are keyed by username.
      const sub = getAttributeValue(cognitoUser, 'sub');

      return {
        id: cognitoUser.Username || cognitoUser.username || email,
        subs: sub ? [sub] : [],
        employeeId,
        fullName: name,
        name,
        email,
        department,
        designation,
        primaryShift,
        role,
        managerEmail,
        location,
        enabled,
        dateOfJoining,
        dateOfExit,
        employmentType,
        deviceCode,
        phone,
        aadhaarNumber,
        panNumber,
        team,
        dateOfBirth,
      };
    });

    // Deduplicate by CANONICAL email, merging a person's duplicate Cognito accounts.
    //
    // Every employee has one account per email domain — @creyentetech.com and
    // @creyenteinfotech.com — each with its own Cognito username and sub. Keying on the
    // raw email left both in the list, which is why the shift roster showed the same
    // person twice, each row carrying only half their data.
    //
    // The surviving row keeps the highest role, fills any blank field from the twin (a
    // federated account often has no employeeId or department while its native
    // counterpart does), and records every account id in linkedIds so callers that key
    // on the Cognito username can look under all of them.
    // designation is defaulted above, so a placeholder is "no data" rather than a real value
    // and must lose to the twin's specific one. department no longer needs this — it is now ''
    // when unset — but goes through the same helper with an empty placeholder so the two
    // fields keep one merge rule.
    const preferSpecific = (primary: string, twin: string, placeholder: string): string => {
      if (primary && primary !== placeholder) return primary;
      if (twin && twin !== placeholder) return twin;
      return primary || twin || placeholder;
    };

    const seen = new Map<string, SimpleUser>();
    mapped.forEach(u => {
      const key = canonicalizeEmail(u.email);
      if (!key) return;

      const existing = seen.get(key);
      if (!existing) {
        seen.set(key, { ...u, linkedIds: [u.id] });
        return;
      }

      // Shared with User Management — see primaryTwin. Previously "highest role, else whichever
      // Cognito listed first", which disagreed with that screen whenever the roles tied.
      const winner = primaryTwin(existing, u);
      const twin = winner === u ? existing : u;

      seen.set(key, {
        ...winner,
        employeeId: winner.employeeId || twin.employeeId,
        department: preferSpecific(winner.department, twin.department, ''),
        designation: preferSpecific(winner.designation, twin.designation, 'Employee'),
        // Not `winner.primaryShift || twin.primaryShift`: the winner is whoever holds the
        // higher role, which for a native/federated pair is arbitrary. Shift follows its
        // own rule, shared with User Management — see mergeShiftCode.
        primaryShift: mergeShiftCode(
          { id: winner.id, shift: winner.primaryShift },
          { id: twin.id, shift: twin.primaryShift }
        ),
        dateOfJoining: winner.dateOfJoining || twin.dateOfJoining,
        dateOfExit: winner.dateOfExit || twin.dateOfExit,
        employmentType: winner.employmentType || twin.employmentType,
        // Merged like the rest: a person's two domain accounts may each carry only half
        // their profile, and the team must survive whichever one wins on role.
        team: winner.team || twin.team,
        dateOfBirth: winner.dateOfBirth || twin.dateOfBirth,
        // Merged for the same reason as team above: a person's two domain accounts may each
        // hold only half the profile, and a payroll identifier must not be lost to whichever
        // account happened to win on role.
        aadhaarNumber: winner.aadhaarNumber || twin.aadhaarNumber,
        panNumber: winner.panNumber || twin.panNumber,
        managerEmail: winner.managerEmail || twin.managerEmail,
        location: winner.location || twin.location,
        enabled: winner.enabled || twin.enabled,
        // Winner first: it is the account whose assignments take precedence on a clash.
        linkedIds: Array.from(new Set([winner.id, ...(existing.linkedIds || [existing.id]), u.id])),
        // Both subs survive the merge: either account could be the one that performed an action,
        // and losing the twin's sub is exactly what leaves an audit row unattributable.
        subs: Array.from(new Set([...(winner.subs || []), ...(twin.subs || [])])),
      });
    });
    const dedupedUsers = Array.from(seen.values());
    console.log('[userLoader] total canonical users resolved:', dedupedUsers.length);
    return dedupedUsers;
  } catch (error) {
    console.error('[userLoader] Failed to load users:', error);
    return [];
  }
}

// Role constants matching the system's role mapping
const REVIEWER_ROLES: string[] = [UserRole.FINANCE_ADMIN, 'Reviewer', 'Finance Admin', 'Finance_Admin', 'FINANCE_ADMIN', 'finance_admin'];
const ADMIN_ROLES: string[] = [UserRole.SUPER_ADMIN, 'Admin', 'Super Admin', 'Super_Admin', 'SUPER_ADMIN', 'super_admin'];

/*
 * Directory cache: a short TTL plus in-flight sharing.
 *
 * Seventeen call sites reach for loadAllUsers() directly and one used the cached variant,
 * so navigating the app re-listed the whole Cognito directory over and over — 74 users
 * fetched, normalised and deduplicated per screen, several times per screen where a page
 * recomputes on 'leave-requests-updated' and friends. That is the bulk of the wait on a
 * local run, and none of it buys anything: the directory does not change between two
 * requests fired seconds apart.
 *
 * Both halves matter. The TTL stops repeat trips; sharing the in-flight promise collapses
 * the burst of simultaneous calls a single page mount produces, which a TTL alone cannot
 * because none of them has resolved yet to populate the cache.
 *
 * 30 seconds is chosen to be shorter than the time it takes to edit a user and navigate to
 * a screen that shows them. Anything that mutates the directory should call
 * clearUserCache() rather than rely on it expiring.
 */
const USERS_CACHE_TTL_MS = 30_000;
let cachedUsers: SimpleUser[] | null = null;
let cachedUsersAt = 0;
let inFlightUsers: Promise<SimpleUser[]> | null = null;

/*
 * Bumped by clearUserCache, and captured by each fetch before it starts.
 *
 * Without it a clear that lands *during* a fetch is silently undone: the request was issued
 * before the edit, so its result is pre-edit data, but it resolves afterwards and repopulates
 * the cache — pinning the stale directory for another full TTL. That is precisely the case
 * clearUserCache exists for, since UserManagement calls it while other screens may already
 * have a load in flight, so the failure mode is "saved the user, every other screen shows the
 * old value for 30 seconds" — the bug the cache was supposed to be invisible to.
 *
 * A generation counter rather than an abort: the in-flight request is still perfectly good as
 * an *answer* to the callers already awaiting it, it just must not become the cached one.
 */
let usersGeneration = 0;

export async function loadAllUsers(): Promise<SimpleUser[]> {
  if (cachedUsers && Date.now() - cachedUsersAt < USERS_CACHE_TTL_MS) return cachedUsers;
  if (inFlightUsers) return inFlightUsers;

  const generation = usersGeneration;
  const pending = fetchAllUsers()
    .then(users => {
      // An empty result is not cached: it is what the loader returns when the request
      // failed, and caching a failure would blank every screen for the next 30 seconds.
      if (users.length > 0 && generation === usersGeneration) {
        cachedUsers = users;
        cachedUsersAt = Date.now();
      }
      return users;
    })
    .finally(() => {
      // Only if it is still ours: a clear during the fetch already dropped it, and a later
      // call may have installed a replacement we must not null out.
      if (inFlightUsers === pending) inFlightUsers = null;
    });
  inFlightUsers = pending;

  return pending;
}

/** @deprecated Kept for callers that predate loadAllUsers being cached; identical now. */
export async function getCachedUsers(): Promise<SimpleUser[]> {
  return loadAllUsers();
}

/** Drop the cached directory. Call after any write that changes a user record. */
export function clearUserCache(): void {
  cachedUsers = null;
  cachedUsersAt = 0;
  profileShiftMapPromise = null;
  // Invalidates any fetch already running, so its pre-edit result cannot land in the cache
  // after this point, and drops it as the promise new callers would be handed.
  usersGeneration++;
  inFlightUsers = null;
}

export async function loadDirectReports(managerEmail: string): Promise<SimpleUser[]> {
  const allUsers = await getCachedUsers();
  // Domain-aware: a manager's reports may name either of their two addresses.
  return allUsers.filter(u => sameIdentity(u.managerEmail, managerEmail));
}

export interface DirectReportsResult {
  /**
   * False when the *directory* could not be loaded, as opposed to this manager genuinely
   * having no reports.
   *
   * loadDirectReports cannot express the difference: fetchAllUsers swallows its errors and
   * returns [], so a failed listUsers call and a manager with an empty team both arrive as an
   * empty array — and callers that scope other data through that array silently report zero of
   * everything. Team Reviews did exactly that, showing "no review records" for a cycle whose
   * records had loaded fine.
   */
  directoryAvailable: boolean;
  reports: SimpleUser[];
}

/**
 * loadDirectReports plus whether the directory itself was readable.
 *
 * An empty directory is treated as unavailable rather than as "nobody exists": the caller is
 * always an authenticated user, so a directory that does not even contain them did not load.
 */
export async function loadDirectReportsWithStatus(
  managerEmail: string
): Promise<DirectReportsResult> {
  const allUsers = await loadAllUsers();
  if (allUsers.length === 0) return { directoryAvailable: false, reports: [] };
  return {
    directoryAvailable: true,
    reports: allUsers.filter(u => sameIdentity(u.managerEmail, managerEmail)),
  };
}

export async function getReviewerAndAdminEmails(): Promise<{ reviewerEmails: string[]; adminEmails: string[] }> {
  const allUsers = await getCachedUsers();
  const reviewerEmails: string[] = [];
  const adminEmails: string[] = [];

  allUsers.forEach(u => {
    if (REVIEWER_ROLES.includes(u.role) || REVIEWER_ROLES.includes(u.role.toLowerCase())) {
      reviewerEmails.push(u.email);
    }
    if (ADMIN_ROLES.includes(u.role) || ADMIN_ROLES.includes(u.role.toLowerCase())) {
      adminEmails.push(u.email);
    }
  });

  return { reviewerEmails, adminEmails };
}

export async function getFinanceAdminEmails(): Promise<string[]> {
  const users = await getCachedUsers();
  return users
    .filter(u => u.role === UserRole.FINANCE_ADMIN || u.role === UserRole.SUPER_ADMIN || String(u.role).toLowerCase().includes('finance') || String(u.role).toLowerCase().includes('super'))
    .map(u => u.email);
}

export async function getManagerEmailForUser(userEmail: string): Promise<string | undefined> {
  const users = await getCachedUsers();
  // Domain-aware: the caller may hold either of this person's two addresses.
  const u = users.find(x => sameIdentity(x.email, userEmail));
  return u?.managerEmail;
}

export async function getUserByEmail(email: string): Promise<SimpleUser | undefined> {
  const users = await getCachedUsers();
  return users.find(x => sameIdentity(x.email, email));
}

export async function getAllUserEmails(): Promise<string[]> {
  const users = await getCachedUsers();
  return users.map(u => u.email);
}

/**
 * Active employees, optionally narrowed to one manager's reports.
 *
 * The manager filter uses sameIdentity, not a lowercased `===`. Everyone here can own two
 * Cognito accounts on two domains (creyentetech.com and creyenteinfotech.com) and this function
 * merges them into one row — but a report's stored managerEmail names whichever of the two
 * addresses was current when it was set. An exact comparison therefore dropped every report
 * filed against the manager's *other* address, so a manager opening Bulk Shift Override saw a
 * short list or none at all, and searching a name they knew was on their team found nobody.
 *
 * loadDirectReports directly above already did this correctly; this one was the copy that did
 * not, which is why the two disagreed about the same manager's team.
 */
export async function loadAllActiveEmployees(managerEmail?: string): Promise<SimpleUser[]> {
  const users = await getCachedUsers();
  /*
   * "Active" means still employed, not merely "the Cognito account is switched on".
   *
   * This used to test `enabled !== false` alone, which misses the ordinary way somebody leaves:
   * HR records a date of exit in User Management and nobody disables the login. That person went
   * on appearing as a current employee everywhere this loader feeds — the Team Leave Matrix,
   * Team Reviews, Manual Attendance and the attendance entry picker — so the organisation
   * structure still listed them months after they had gone.
   *
   * hasLeftOrganisation is the one definition of the question, already used by the Shift Roster
   * and Leave Reports. It covers BOTH routes out: a past exit date, or a disabled account.
   *
   * A FUTURE exit date deliberately does not count. Somebody serving notice is still here, still
   * needs a shift and still belongs in their manager's reports — removing them the moment a
   * resignation is recorded is exactly the period their cover has to be planned.
   */
  const today = todayIso();
  const active = users.filter(u => isCurrentEmployee(u, today));
  if (managerEmail) {
    return active.filter(u => sameIdentity(u.managerEmail, managerEmail));
  }
  return active;
}
